import React, { useState, useEffect, useCallback } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { supabase } from "../utils/supabaseClient";
import "../styles/VisitorStatsChart.css";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const VisitorStatsChart = () => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });
  const [loading, setLoading] = useState(true);

  const fetchVisitorStats = useCallback(async () => {
    try {
      // Get the date 30 days ago
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      thirtyDaysAgo.setHours(0, 0, 0, 0);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { data, error } = await supabase
        .from("page_visits")
        .select("visit_date, visit_count")
        .eq("page_name", "home")
        .gte("visit_date", thirtyDaysAgo.toISOString().split("T")[0])
        .order("visit_date", { ascending: true });

      if (error) throw error;

      // Generate all dates for the last 30 days
      const allDates = [];
      let currentDate = new Date(thirtyDaysAgo);
      while (currentDate <= today) {
        allDates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Create a map of existing data
      const visitsMap = new Map(
        data.map((item) => [item.visit_date, item.visit_count])
      );

      // Create final arrays with all dates, filling in zeros for missing dates
      const labels = allDates.map((date) =>
        date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        })
      );
      const visitCounts = allDates.map(
        (date) => visitsMap.get(date.toISOString().split("T")[0]) || 0
      );

      setChartData({
        labels,
        datasets: [
          {
            label: "Daily Visitors",
            data: visitCounts,
            borderColor: "#fc0500",
            backgroundColor: "rgba(252, 5, 0, 0.05)",
            tension: 0.3,
            fill: true,
            pointBackgroundColor: "#fc0500",
            pointBorderColor: "#ffffff",
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            borderWidth: 3,
          },
        ],
      });
      setLoading(false);
    } catch (err) {
      console.error("Error fetching visitor stats:", err);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVisitorStats();
    // Fetch data every 5 minutes
    const interval = setInterval(fetchVisitorStats, 300000);
    return () => clearInterval(interval);
  }, [fetchVisitorStats]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 14,
            weight: "500",
          },
        },
      },
      title: {
        display: true,
        text: "Visitor Statistics (Last 30 Days)",
        color: "#333333",
        font: {
          size: 18,
          weight: "600",
        },
        padding: {
          bottom: 30,
        },
      },
      tooltip: {
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        titleColor: "#333333",
        bodyColor: "#666666",
        bodyFont: {
          size: 14,
        },
        titleFont: {
          size: 14,
          weight: "600",
        },
        padding: 12,
        boxPadding: 6,
        borderColor: "rgba(0, 0, 0, 0.1)",
        borderWidth: 1,
        displayColors: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
          color: "#666666",
          font: {
            size: 12,
          },
        },
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
          drawBorder: false,
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#666666",
          font: {
            size: 12,
          },
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
  };

  if (loading) {
    return (
      <div className="visitor-stats-chart-container loading">
        <div className="loading-spinner">Loading statistics...</div>
      </div>
    );
  }

  return (
    <div className="visitor-stats-chart-container">
      <Line data={chartData} options={options} />
    </div>
  );
};

export default VisitorStatsChart;
